import { authFetch, type FetchLike } from "@/auth/http";
import { API_BASE_URL } from "@/api/base";

/**
 * What the SHELL needs to know about a production: what it is CALLED, and enough about it to say so
 * in a line under the name.
 *
 * 🔴 A uuid is not a context a person can read. The sidebar says "you are inside Wilder Hearts", so
 * the identity has to come from somewhere — and it has to be the SHELL that asks, because the shell
 * draws that header while `modules/costs` (which never lists productions) is the thing on screen.
 * Asking the API with the held session is the only answer that works from every route, including a
 * link somebody pasted into a fresh tab.
 *
 * Best-effort, exactly like `auth/profile.ts`: a production that 404s, a 401 before sign-in, or an
 * offline first paint each fall back to "not known". None of them may turn a working navigation into
 * a failure — the context is still the id on the URL, which is what every module reads.
 *
 * `GET /productions/:id` (services/production/api.ts) answers `ProductionDetail`; `GET /productions`
 * answers the list the top bar's switcher offers. The shell declares the few fields it reads rather
 * than importing the Encore app's types — a module or app never imports an Encore app's sources
 * (CLAUDE.md).
 */
export const PRODUCTIONS_PATH = "/productions";
export const productionPath = (productionId: string): string => `${PRODUCTIONS_PATH}/${encodeURIComponent(productionId)}`;

/** The shell's own reading of a production. Every field but `id` may be absent — see the note above. */
export interface ProductionIdentity {
  id: string;
  title: string | null;
  /**
   * 🔴 The show this node is part of, where it is part of one (D143).
   *
   * Devin, inside Season 1 after the first split: *"Nothing is now showing me that this is Tuiskoms
   * Season 1 — just identifying Season One."* "Season 1" is the same three characters on every show
   * ever made; away from its parent's wall it identifies nothing. Null for a root, which needs no
   * qualifier because its own title IS the show.
   */
  parentTitle: string | null;
  /** "slate" · "production" · "season" · "block" · "episode" — the API's vocabulary, not re-declared as a union. */
  kind: string | null;
  /** ISO 3166-1 alpha-2 codes declared for the `production` dimension (D16), in the order given. */
  territories: string[];
  /** ISO date — commencement of principal photography (D13's pin date). */
  principalPhotographyFrom: string | null;
  /**
   * Who the show is FOR, in the words the participation froze — the commissioner where there is one.
   * Null when nobody has been recorded; the subtitle then says nothing rather than a dash.
   */
  commissionedBy: string | null;
}

/** One row of the top bar's switcher. */
export interface ProductionChoice {
  id: string;
  title: string;
  kind: string | null;
}

const text = (value: unknown): string | null => (typeof value === "string" && value.trim() !== "" ? value.trim() : null);

interface ProductionBody {
  id?: unknown;
  title?: unknown;
  kind?: unknown;
  principalPhotographyFrom?: unknown;
  territories?: unknown;
  parties?: unknown;
  parentTitle?: unknown;
}

/**
 * The territories a producer would name the show by. Only the `production` dimension — a production
 * shot in South Africa for a company incorporated in the UK is "ZA" to the people making it, and
 * putting all four dimensions in one subtitle would read as noise (D16 keeps them separate for a
 * reason).
 */
function productionTerritories(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const codes = raw
    .filter((entry): entry is { dimension?: unknown; countryCode?: unknown } => typeof entry === "object" && entry !== null)
    .filter((entry) => entry.dimension === "production")
    .map((entry) => text(entry.countryCode))
    .filter((code): code is string => code !== null);
  return [...new Set(codes)];
}

/**
 * The one party a sidebar has room for. The service already orders them commissioner-first, so this
 * takes the first NAMED one rather than re-deciding the question in a second place.
 */
function commissionedBy(raw: unknown): string | null {
  if (!Array.isArray(raw)) return null;
  for (const entry of raw) {
    if (typeof entry !== "object" || entry === null) continue;
    const name = text((entry as { name?: unknown }).name);
    if (name !== null) return name;
  }
  return null;
}

function toIdentity(body: ProductionBody, fallbackId: string): ProductionIdentity {
  return {
    id: text(body.id) ?? fallbackId,
    title: text(body.title),
    parentTitle: text(body.parentTitle),
    kind: text(body.kind),
    territories: productionTerritories(body.territories),
    principalPhotographyFrom: text(body.principalPhotographyFrom),
    commissionedBy: commissionedBy(body.parties),
  };
}

/** The production behind an id, or null where it could not be had. Never throws. */
export async function fetchProduction(
  productionId: string,
  base: string = API_BASE_URL,
  fetchImpl?: FetchLike,
): Promise<ProductionIdentity | null> {
  try {
    const response = await authFetch(`${base}${productionPath(productionId)}`, undefined, fetchImpl);
    if (!response.ok) return null;
    const body = (await response.json()) as ProductionBody;
    return toIdentity(body, productionId);
  } catch {
    return null;
  }
}

interface ProductionListBody {
  items?: unknown;
}

/**
 * What the top bar's switcher offers — every production this session can open, titles only.
 *
 * A row with no title is dropped rather than drawn as a blank line: the switcher exists so a producer
 * with three shows can move between them by NAME, and an unnamed row is not a choice anyone can make.
 * Empty on any failure, which the switcher reads as "nothing to switch to" and simply does not draw.
 */
export async function fetchProductions(base: string = API_BASE_URL, fetchImpl?: FetchLike): Promise<ProductionChoice[]> {
  try {
    const response = await authFetch(`${base}${PRODUCTIONS_PATH}`, undefined, fetchImpl);
    if (!response.ok) return [];
    const body = (await response.json()) as ProductionListBody;
    if (!Array.isArray(body.items)) return [];
    return body.items
      .filter((item): item is ProductionBody => typeof item === "object" && item !== null)
      .map((item) => ({ id: text(item.id), title: text(item.title), kind: text(item.kind) }))
      .filter((item): item is ProductionChoice => item.id !== null && item.title !== null);
  } catch {
    return [];
  }
}

/**
 * 🔴 **WHAT THIS PERSON IS ON THIS PRODUCTION** (D157, PNK-187) — `GET /productions/:id/me`.
 *
 * The rail inside a production is drawn from this and nothing else: which production roles the caller
 * holds, whether they created or administer it, how they are engaged on it, and the visibility scopes
 * all of that adds up to (the server's own union, `mayRead` applied to every scope — never re-derived
 * here). Like every other shell read it is NEVER authority: the gate and each handler re-check on every
 * request. It only decides what is DRAWN, and it fails CLOSED (`nav/navStanding.ts`).
 */
export type EngagedAs = "cast" | "crew" | "background";

export interface ProductionStanding {
  productionId: string;
  /** Its name, from the standing itself — the only name a person engaged without a role can read. */
  title: string | null;
  /** Production role names (`producer`, `production_accountant`, …) — the server's vocabulary, not re-declared. */
  roles: string[];
  /** The caller created it. */
  owner: boolean;
  /** The caller administers it (D137): its creator, or an owner/administrator of its company. */
  administers: boolean;
  /** How the caller's own engagements put them on it — empty when they are not engaged. */
  engagedAs: EngagedAs[];
  /**
   * A live engagement of theirs on a node BENEATH this one (D164a) — an actor on Episode 1 who opened the show. It
   * opens the one engaged door (My production) and nothing else: the show's working documents are not theirs.
   */
  engagedBeneath: boolean;
  /** `VisibilityScope` names the caller may read on it. */
  scopes: string[];
  /** Server-side switches that are on and draw doors on it (D170: `productionCalendar`). Empty = every one off. */
  features: string[];
}

export const standingPath = (productionId: string): string => `${productionPath(productionId)}/me`;

/** Nobody-in-particular: what a 404, a 401, an offline read or a malformed body all come to. */
export const noStanding = (productionId: string): ProductionStanding => ({
  productionId,
  title: null,
  roles: [],
  owner: false,
  administers: false,
  engagedAs: [],
  engagedBeneath: false,
  scopes: [],
  features: [],
});

const strings = (raw: unknown): string[] =>
  Array.isArray(raw) ? [...new Set(raw.filter((entry): entry is string => typeof entry === "string" && entry.trim() !== ""))] : [];

const ENGAGED_AS: ReadonlyArray<EngagedAs> = ["cast", "crew", "background"];

/** The answers that mean "you have nothing here" — the server looked and said no. */
const REFUSED: ReadonlyArray<number> = [403, 404];

/**
 * The caller's standing on one production. Never throws. A REFUSAL (403/404) is an answer — no
 * standing — and the rail fails closed on it. Anything else (401 before the session is live, a 5xx, the
 * network) is null: nobody asked the question yet, so it is not cached as the answer.
 *
 * 🔴 Found in Chrome, switching Pieter → Naledi on a production URL: the check fired in the gap
 * between sign-out and the new session, came back 401, and was kept as "nothing on this production is
 * open to you" — beside the page showing her own four shoot days on it.
 */
export async function fetchStanding(productionId: string, base: string = API_BASE_URL, fetchImpl?: FetchLike): Promise<ProductionStanding | null> {
  try {
    const response = await authFetch(`${base}${standingPath(productionId)}`, undefined, fetchImpl);
    if (REFUSED.includes(response.status)) return noStanding(productionId);
    if (!response.ok) return null;
    const body = (await response.json()) as Record<string, unknown>;
    return {
      productionId: text(body.productionId) ?? productionId,
      title: text(body.title),
      roles: strings(body.roles),
      owner: body.owner === true,
      administers: body.administers === true,
      engagedAs: strings(body.engagedAs).filter((entry): entry is EngagedAs => (ENGAGED_AS as readonly string[]).includes(entry)),
      engagedBeneath: body.engagedBeneath === true,
      scopes: strings(body.scopes),
      features: strings(body.features),
    };
  } catch {
    return null;
  }
}
